import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiGet } from "./api.js";

/**
 * Sprint 26 Wave A — feature-flag client for the admin/operator app.
 *
 * Mirrors `apps/web/src/lib/featureFlags.ts` against the same
 * `GET /v1/feature-flags` endpoint. The admin app additionally
 * exposes the full resolved flag table (including
 * `internal_admin_only` and `hidden_disabled` entries) on the
 * Sprint 26 QA-Harness panel so an operator can audit the rollout
 * state.
 */

export interface Sprint26FlagSnapshot {
  readonly key: string;
  readonly wave: string;
  readonly visibility: "public_testable" | "internal_admin_only" | "hidden_disabled";
  readonly enabled: boolean;
  readonly visible: boolean;
  readonly source: "default" | "env";
  readonly description: string;
}

export interface FeatureFlagsResponse {
  readonly sprint: 26;
  readonly resolvedAt: string;
  readonly viewer: {
    readonly roles: readonly string[];
    readonly anonymous: boolean;
  };
  readonly flags: ReadonlyArray<Sprint26FlagSnapshot>;
}

export const FEATURE_FLAGS_KEY = ["feature-flags", "sprint26"] as const;

export function useSprint26FeatureFlags() {
  return useQuery<FeatureFlagsResponse, Error>({
    queryKey: FEATURE_FLAGS_KEY,
    queryFn: ({ signal }) => apiGet<FeatureFlagsResponse>("/v1/feature-flags", undefined, signal),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSprint26FlagVisible(key: string): {
  visible: boolean;
  loading: boolean;
} {
  const { data, isLoading } = useSprint26FeatureFlags();
  return useMemo(() => {
    if (!data) return { visible: false, loading: isLoading };
    // Defensive: tolerate a malformed response so a stubbed fetch in
    // a sibling test cannot crash the panel.
    const flags = Array.isArray(data.flags) ? data.flags : [];
    const f = flags.find((flag) => flag.key === key);
    return { visible: f?.visible === true, loading: false };
  }, [data, isLoading, key]);
}
